import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef, useState } from "react";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import { cn } from "@/shared/lib/cn";

gsap.registerPlugin(useGSAP);

const submitButtonMotion = {
  hovered: {
    boxShadow: "0 10px 24px rgba(0, 97, 254, 0.28)",
    duration: 0.2,
    ease: "power2.out",
    scale: 1.06,
    y: -2,
  },
  idle: {
    boxShadow: "0 0 0 rgba(0, 97, 254, 0)",
    duration: 0.24,
    ease: "power3.out",
    scale: 1,
    y: 0,
  },
  pressed: {
    boxShadow: "0 4px 12px rgba(0, 97, 254, 0.22)",
    duration: 0.1,
    ease: "power2.out",
    scale: 0.94,
    y: 0,
  },
} as const;

type IconButtonInteraction = keyof typeof submitButtonMotion;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface IconButtonProps {
  ariaExpanded?: boolean;
  ariaHasPopup?: AriaButtonProps["aria-haspopup"];
  className?: string;
  hasHoverMotion?: boolean;
  icon: string;
  hoverIcon?: string;
  iconFrameClassName: string;
  isDisabled?: boolean;
  label: string;
  onPress?: AriaButtonProps["onPress"];
  tone?: "focus" | "plain" | "muted" | "theme";
}

export function ChatIconButton({
  ariaExpanded,
  ariaHasPopup,
  className,
  hasHoverMotion = false,
  icon,
  hoverIcon,
  iconFrameClassName,
  isDisabled = false,
  label,
  onPress,
  tone = "plain",
}: IconButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isHoveredRef = useRef(false);
  const [interaction, setInteraction] =
    useState<IconButtonInteraction>("idle");

  useGSAP(
    () => {
      const button = buttonRef.current;

      if (
        !button ||
        !hasHoverMotion ||
        isDisabled ||
        prefersReducedMotion()
      ) {
        if (button) {
          gsap.killTweensOf(button);
          gsap.set(button, {
            clearProps: "boxShadow,transform,willChange",
          });
        }
        return;
      }

      const motion = submitButtonMotion[interaction];
      gsap.set(button, { willChange: "transform, box-shadow" });
      gsap.to(button, {
        boxShadow: motion.boxShadow,
        duration: motion.duration,
        ease: motion.ease,
        overwrite: "auto",
        scale: motion.scale,
        y: motion.y,
        onComplete:
          interaction === "idle"
            ? () => {
                gsap.set(button, {
                  clearProps: "boxShadow,transform,willChange",
                });
              }
            : undefined,
      });
    },
    {
      dependencies: [hasHoverMotion, interaction, isDisabled],
      scope: buttonRef,
    },
  );

  const startHover = () => {
    isHoveredRef.current = true;
    setInteraction("hovered");
  };

  const endHover = () => {
    isHoveredRef.current = false;
    setInteraction("idle");
  };

  const startPress = () => {
    setInteraction("pressed");
  };

  const endPress = () => {
    setInteraction(isHoveredRef.current ? "hovered" : "idle");
  };

  return (
    <div
      className={cn("relative z-10 size-12 shrink-0", className)}
      data-chat-layout-item=""
    >
      <AriaButton
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={label}
        className={({ isFocusVisible }) =>
          cn(
            "flex size-12 appearance-none items-center justify-center overflow-clip rounded-[32px] border-0 p-3 outline-none",
            tone === "plain" && "bg-[var(--merry-chat-white-1)]",
            tone === "muted" && "bg-[var(--merry-chat-black-5)]",
            tone === "focus" && "bg-[var(--merry-button-theme-6)]",
            tone === "theme" && "bg-[var(--merry-chat-theme)]",
            isFocusVisible &&
              "ring-2 ring-[var(--merry-chat-theme)] ring-offset-2",
          )
        }
        data-chat-button-motion={hasHoverMotion ? interaction : undefined}
        data-chat-button-tone={tone}
        isDisabled={isDisabled}
        onHoverEnd={hasHoverMotion ? endHover : undefined}
        onHoverStart={hasHoverMotion ? startHover : undefined}
        onPress={onPress}
        onPressEnd={hasHoverMotion ? endPress : undefined}
        onPressStart={hasHoverMotion ? startPress : undefined}
        ref={buttonRef}
      >
        {({ isHovered }) => <span aria-hidden="true" className="relative block size-6 shrink-0">
          <span className={cn("absolute", iconFrameClassName)}>
            <img
              alt=""
              className="pointer-events-none block size-full max-w-none select-none"
              draggable="false"
              src={isHovered && hoverIcon ? hoverIcon : icon}
            />
          </span>
        </span>}
      </AriaButton>
    </div>
  );
}

